import { OpenInNew } from '@mui/icons-material';
import { ReactNode } from 'react';

import { ClaudeConnectorsMock } from 'components/shared/bumperMcp/ClaudeMocks';

import {
  CLAUDE_CONNECTORS_URL,
  MCP_OAUTH_CLIENT_ID,
  MCP_SERVER_URL,
} from '../../constants';
import { CopyButton } from '../CopyButton';
import {
  CopyableRoot,
  CopyableValueText,
  IllustrationPanel,
  OpenConnectorsLink,
  SectionText,
  SectionTitle,
  SetupLayout,
  StepBody,
  StepLine,
  StepNote,
  StepNumber,
  StepRail,
  StepRow,
  StepText,
  StepTitle,
  StepsColumn,
} from '../styledComponents';

interface StepProps {
  number: number;
  title: string;
  isLast?: boolean;
  children: ReactNode;
}

const Step = ({ number, title, isLast = false, children }: StepProps) => (
  <StepRow>
    <StepRail>
      <StepNumber>{number}</StepNumber>
      {!isLast && <StepLine />}
    </StepRail>
    <StepBody isLast={isLast}>
      <StepTitle>{title}</StepTitle>
      {children}
    </StepBody>
  </StepRow>
);

interface CopyableValueProps {
  value: string;
  valueTestId: string;
  copyTestId: string;
}

const CopyableValue = ({
  value,
  valueTestId,
  copyTestId,
}: CopyableValueProps) => (
  <CopyableRoot>
    <CopyableValueText data-testid={valueTestId}>{value}</CopyableValueText>
    <CopyButton value={value} testId={copyTestId} />
  </CopyableRoot>
);

export const ClaudeTab = () => (
  <SetupLayout>
    <StepsColumn>
      <SectionTitle>Connect Bumper in seconds</SectionTitle>
      <SectionText>
        Setting up Bumper MCP is simple and takes just a few moments.
      </SectionText>
      <Step number={1} title="Go to Claude → Customize">
        <StepText>
          In Claude desktop or claude.ai, go to{' '}
          <strong>Customize → Connectors</strong>.
        </StepText>
      </Step>
      <Step number={2} title="Add custom connector">
        <StepText>
          Name it <strong>Bumper</strong> and paste the link:
        </StepText>
        <CopyableValue
          value={MCP_SERVER_URL}
          valueTestId="mcp-server-url"
          copyTestId="mcp-copy-url-button"
        />
      </Step>
      <Step number={3} title="Set up the OAuth client">
        <StepText>
          Under <strong>OAuth client</strong>, choose{' '}
          <strong>Use your own OAuth client</strong> and enter this OAuth client
          ID. Leave the client secret blank:
        </StepText>
        <CopyableValue
          value={MCP_OAUTH_CLIENT_ID}
          valueTestId="mcp-oauth-client-id"
          copyTestId="mcp-copy-client-id-button"
        />
      </Step>
      <Step number={4} title="Connect and sign in" isLast>
        <StepText>
          Click <strong>Add → Connect</strong>, sign in with your Bumper
          account. Then ask Claude to run a vehicle report.
        </StepText>
        <StepNote>
          Read-only report access. Revoke anytime from Claude or your Bumper
          account.
        </StepNote>
      </Step>
      <OpenConnectorsLink
        href={CLAUDE_CONNECTORS_URL}
        target="_blank"
        rel="noopener noreferrer"
      >
        <OpenInNew sx={{ fontSize: 18 }} aria-hidden />
        Open Claude connectors
      </OpenConnectorsLink>
    </StepsColumn>
    <IllustrationPanel>
      <ClaudeConnectorsMock />
    </IllustrationPanel>
  </SetupLayout>
);
